'use client';

import React, { useState, useEffect } from 'react';
import { RefreshCw, Zap } from 'lucide-react';

interface DashboardTopbarProps {
  onRefresh: () => void;
  refreshing: boolean;
  lastUpdated: string | null;
}

export default function DashboardTopbar({
  onRefresh,
  refreshing,
  lastUpdated,
}: DashboardTopbarProps) {
  const [timeStr, setTimeStr] = useState('');

  useEffect(() => {
    function tick() {
      const now = new Date();
      setTimeStr(
        `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
      );
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 h-14 flex items-center justify-between">
        {/* Logo + Brand */}
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center flex-shrink-0">
            <Zap size={14} className="text-primary-foreground" strokeWidth={2.5} />
          </div>
          <span className="font-semibold text-sm text-foreground tracking-tight hidden sm:block">
            BoersenstrompreisDashboard
          </span>
          <span className="font-semibold text-sm text-foreground tracking-tight sm:hidden">
            Börsenstrom
          </span>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-3">
          {/* Live clock */}
          <span className="font-tabular text-xs text-muted-foreground hidden md:block">
            {timeStr}
          </span>

          {/* Auto-refresh indicator */}
          <span className="label-caps hidden sm:block">
            Auto ·{' '}
            <span className="text-primary">60s</span>
          </span>

          {/* Manual refresh button */}
          <button
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Daten manuell aktualisieren"
            title="Jetzt aktualisieren"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-muted text-foreground text-xs font-medium
              hover:bg-secondary hover:border-ring transition-all duration-150
              active:scale-95
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw
              size={13}
              className={refreshing ? 'refresh-spin' : ''}
              strokeWidth={2.2}
            />
            <span className="hidden sm:inline">
              {refreshing ? 'Lädt…' : 'Aktualisieren'}
            </span>
          </button>
        </div>
      </div>
    </header>
  );
}